import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AuthService } from '../../../core/auth/auth';
import { AchievementEditModal } from '../achievement-edit-modal/achievement-edit-modal';
import {
  Achievement,
  AchievementDefinition,
  AddAchievementInput,
  AwardType,
  Competition,
  CompetitionsService,
  DistributionMode,
  LeaderboardEntry,
} from '../../../core/competitions/competitions';

type AddMode = 'catalog' | 'custom';

const FORM_DEFAULTS = {
  achievementDefinitionId: null as number | null,
  name: '',
  description: '',
  awardType: 'POINTS' as AwardType,
  pointsValue: null as number | null,
  prizeDescription: '',
  prizeDollarValue: null as number | null,
  distributionMode: 'DUPLICATED' as DistributionMode,
};

/**
 * F-021 Milestone C — owner-facing achievement management: browse the
 * global catalog (11 seeded entries), attach one, or create a fully
 * custom one, up to the plan's maxAchievements cap. Any competition
 * member can view this page (achievements aren't sensitive — same
 * visibility as the read-only list on the detail page); only the owner,
 * and only on a paid plan, sees the add/remove controls (mirrors the
 * backend's own gating in CompetitionAchievementsService).
 *
 * F-021 Milestone D adds manual award-granting per achievement — the
 * backend itself permits owner-or-cohost, but this UI is owner-only:
 * no cohost-facing surface exists anywhere in the frontend yet to
 * extend, so restricting here doesn't regress an existing capability.
 */
@Component({
  selector: 'app-competition-achievements',
  imports: [ReactiveFormsModule, RouterLink, AchievementEditModal],
  templateUrl: './competition-achievements.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './competition-achievements.css',
})
export class CompetitionAchievements implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly competitions = inject(CompetitionsService);
  private readonly auth = inject(AuthService);

  competitionId!: number;

  readonly loading = signal(true);
  readonly errorMessage = signal<string | null>(null);
  readonly competition = signal<Competition | null>(null);
  readonly achievements = signal<Achievement[]>([]);
  readonly definitions = signal<AchievementDefinition[]>([]);
  /** F-021 Milestone D — only fetched for canManage() (owner + paid),
   *  since that's the only case that uses it (the contestant picker and
   *  current-holders display for manual awarding). */
  readonly leaderboard = signal<LeaderboardEntry[]>([]);

  readonly isOwner = computed(() => {
    const c = this.competition();
    const user = this.auth.user();
    return !!c && !!user && c.ownerId === user.id;
  });
  readonly canManage = computed(
    () => this.isOwner() && this.competition()?.ownerPlan !== 'FREE',
  );

  /** Catalog entries this competition doesn't already have an instance of —
   *  the backend would 409 on a duplicate anyway, but filtering client-side
   *  avoids offering a choice that's guaranteed to fail. */
  readonly availableDefinitions = computed(() => {
    const attachedIds = new Set(
      this.achievements()
        .map((a) => a.achievementDefinitionId)
        .filter((id): id is number => id !== null),
    );
    return this.definitions().filter((d) => !attachedIds.has(d.id));
  });

  /** F-021 Milestone D — every contestant, each with the achievements
   *  they already hold (from the leaderboard's per-contestant awards —
   *  the same fetch also supplies "who can I award this to" without a
   *  separate listContestants() call, since LeaderboardEntry already
   *  carries the real contestantId that AwardAchievementDto needs, unlike
   *  ContestantSummary which only exposes the userId). */
  readonly holdersByAchievement = computed(() => {
    const map = new Map<number, { contestantId: number; displayName: string }[]>();
    for (const entry of this.leaderboard()) {
      for (const award of entry.awards) {
        const list = map.get(award.achievementId) ?? [];
        // One entry per contestant, even if they hold several awards of
        // the same achievement — the split divides among people, not awards.
        if (!list.some((h) => h.contestantId === entry.contestantId)) {
          list.push({ contestantId: entry.contestantId, displayName: entry.displayName });
        }
        map.set(award.achievementId, list);
      }
    }
    return map;
  });

  holdersFor(a: Achievement): { contestantId: number; displayName: string }[] {
    return this.holdersByAchievement().get(a.id) ?? [];
  }

  /** A SPLIT achievement is only actually split when more than one
   *  contestant holds it — a lone winner just gets the whole thing, so none
   *  of the split handling applies. */
  isShared(a: Achievement): boolean {
    return a.distributionMode === 'SPLIT' && this.holdersFor(a).length > 1;
  }

  /** Best-guess even split of a shared SPLIT achievement among the
   *  contestants who currently hold it, rounded up to a whole number. Points are split from
   *  the achievement's points value; a prize only from its Dollar Value —
   *  without one there is nothing to divide, so no guess is made. The owner
   *  can always override it with the real amount. */
  splitSuggestion(
    a: Achievement,
  ): { amount: number; total: number; holders: number; unit: 'pts' | '$' } | null {
    if (!this.isShared(a)) return null;
    const holders = this.holdersFor(a).length;

    const isPoints = a.awardType === 'POINTS';
    const total = isPoints
      ? a.pointsValue
      : a.prizeDollarValue === null
        ? null
        : Number(a.prizeDollarValue);
    if (total === null || !(total > 0)) return null;

    return {
      amount: Math.ceil(total / holders),
      total,
      holders,
      unit: isPoints ? 'pts' : '$',
    };
  }

  /** F-021 Milestone D — mirrors the backend's assertManuallyAwardable():
   *  a fully-automated DUPLICATED catalog achievement is the scoring
   *  engine's job, not a human's — manual awarding only ever applies to
   *  a custom achievement (no achievementDefinitionId), a non-automatable
   *  catalog one, or a SPLIT one that more than one contestant has won
   *  (someone has to set each share — a lone winner needs no split). */
  isManuallyAwardable(a: Achievement): boolean {
    // Age (fixed, no catalog definition) is computed per death — never manual.
    if (a.isFixed && a.achievementDefinitionId === null) return false;
    const automatable = a.achievementDefinition?.isAutomatable ?? false;
    return !automatable || this.isShared(a);
  }

  /** The achievement currently open in the edit modal, if any. */
  readonly editing = signal<Achievement | null>(null);

  /** Everything but Age can be edited — Age's points are computed per death. */
  isEditable(a: Achievement): boolean {
    return this.canManage() && !(a.isFixed && a.achievementDefinitionId === null);
  }

  onEdited(updated: Achievement): void {
    this.achievements.update((list) =>
      list.map((a) => (a.id === updated.id ? updated : a)),
    );
    this.editing.set(null);
  }

  readonly awardingAchievementId = signal<number | null>(null);
  readonly awarding = signal(false);
  readonly awardError = signal<string | null>(null);

  readonly awardForm = new FormGroup({
    contestantId: new FormControl<number | null>(null),
    splitAmount: new FormControl<number | null>(null),
  });

  readonly mode = signal<AddMode>('catalog');
  readonly adding = signal(false);
  readonly addError = signal<string | null>(null);

  readonly confirmingRemoveId = signal<number | null>(null);
  readonly removing = signal(false);
  readonly removeError = signal<string | null>(null);

  readonly form = new FormGroup({
    achievementDefinitionId: new FormControl<number | null>(
      FORM_DEFAULTS.achievementDefinitionId,
    ),
    name: new FormControl(FORM_DEFAULTS.name, { nonNullable: true }),
    description: new FormControl(FORM_DEFAULTS.description, { nonNullable: true }),
    awardType: new FormControl(FORM_DEFAULTS.awardType, { nonNullable: true }),
    pointsValue: new FormControl<number | null>(FORM_DEFAULTS.pointsValue),
    prizeDescription: new FormControl(FORM_DEFAULTS.prizeDescription, {
      nonNullable: true,
    }),
    prizeDollarValue: new FormControl<number | null>(
      FORM_DEFAULTS.prizeDollarValue,
    ),
    distributionMode: new FormControl(FORM_DEFAULTS.distributionMode, {
      nonNullable: true,
    }),
  });

  /** The form's live awardType, as a signal — a plain FormControl's `.value`
   *  isn't itself reactive in a zoneless template, so conditionally showing
   *  the points-vs-prize fields needs this instead of reading `.value` directly. */
  readonly awardType = toSignal(this.form.controls.awardType.valueChanges, {
    initialValue: this.form.controls.awardType.value,
  });

  async ngOnInit(): Promise<void> {
    this.competitionId = Number(this.route.snapshot.paramMap.get('id'));
    await this.load();
  }

  displayName(a: Achievement): string {
    return a.achievementDefinition?.name ?? a.name ?? 'Custom Achievement';
  }

  /** The catalog entry currently chosen in the add form, so its
   *  description can be shown beneath the dropdown. */
  selectedDefinition(): AchievementDefinition | null {
    const id = this.form.controls.achievementDefinitionId.value;
    return this.availableDefinitions().find((d) => d.id === id) ?? null;
  }

  displayDescription(a: Achievement): string | null {
    return a.achievementDefinition?.description ?? a.description;
  }

  async addAchievement(): Promise<void> {
    const raw = this.form.getRawValue();
    if (this.mode() === 'catalog' && raw.achievementDefinitionId == null) {
      this.addError.set('Pick an achievement from the catalog.');
      return;
    }
    if (
      this.mode() === 'custom' &&
      (!raw.name.trim() || !raw.description.trim())
    ) {
      this.addError.set(
        'Name and description are required for a custom achievement.',
      );
      return;
    }
    if (raw.awardType === 'POINTS' && raw.pointsValue == null) {
      this.addError.set('Points value is required for a points award.');
      return;
    }
    if (raw.awardType === 'PRIZE' && !raw.prizeDescription.trim()) {
      this.addError.set('Prize description is required for a prize award.');
      return;
    }

    this.adding.set(true);
    this.addError.set(null);
    try {
      const input: AddAchievementInput =
        this.mode() === 'catalog'
          ? {
              achievementDefinitionId: raw.achievementDefinitionId ?? undefined,
              awardType: raw.awardType,
              pointsValue:
                raw.awardType === 'POINTS' ? raw.pointsValue ?? undefined : undefined,
              prizeDescription:
                raw.awardType === 'PRIZE'
                  ? raw.prizeDescription.trim()
                  : undefined,
              prizeDollarValue:
                raw.awardType === 'PRIZE'
                  ? raw.prizeDollarValue ?? undefined
                  : undefined,
              distributionMode: raw.distributionMode,
            }
          : {
              name: raw.name.trim(),
              description: raw.description.trim(),
              awardType: raw.awardType,
              pointsValue:
                raw.awardType === 'POINTS' ? raw.pointsValue ?? undefined : undefined,
              prizeDescription:
                raw.awardType === 'PRIZE'
                  ? raw.prizeDescription.trim()
                  : undefined,
              prizeDollarValue:
                raw.awardType === 'PRIZE'
                  ? raw.prizeDollarValue ?? undefined
                  : undefined,
              distributionMode: raw.distributionMode,
            };

      const created = await this.competitions.addAchievement(
        this.competitionId,
        input,
      );
      this.achievements.update((current) => [...current, created]);
      this.form.reset(FORM_DEFAULTS);
    } catch (err) {
      this.addError.set(this.errorMessageFor(err));
    } finally {
      this.adding.set(false);
    }
  }

  openAward(a: Achievement): void {
    this.awardingAchievementId.set(a.id);
    this.awardError.set(null);
    this.awardForm.reset({
      contestantId: null,
      splitAmount: this.splitSuggestion(a)?.amount ?? null,
    });
  }

  closeAward(): void {
    this.awardingAchievementId.set(null);
  }

  async submitAward(a: Achievement): Promise<void> {
    const raw = this.awardForm.getRawValue();
    if (raw.contestantId == null) {
      this.awardError.set('Pick a contestant.');
      return;
    }
    if (a.distributionMode === 'SPLIT' && raw.splitAmount == null) {
      this.awardError.set('A split amount is required for this achievement.');
      return;
    }

    this.awarding.set(true);
    this.awardError.set(null);
    try {
      await this.competitions.awardAchievement(this.competitionId, a.id, {
        contestantId: raw.contestantId,
        splitAmount:
          a.distributionMode === 'SPLIT' ? raw.splitAmount ?? undefined : undefined,
      });
      // Re-fetch rather than patch client-side — awardManually() may have
      // just updated an existing award's splitAmount instead of creating
      // a new one, and the leaderboard is the source of truth either way.
      this.leaderboard.set(await this.competitions.getLeaderboard(this.competitionId));
      this.awardingAchievementId.set(null);
    } catch (err) {
      this.awardError.set(this.errorMessageFor(err));
    } finally {
      this.awarding.set(false);
    }
  }

  async confirmRemove(achievement: Achievement): Promise<void> {
    this.removing.set(true);
    this.removeError.set(null);
    try {
      await this.competitions.removeAchievement(
        this.competitionId,
        achievement.id,
      );
      this.achievements.update((current) =>
        current.filter((a) => a.id !== achievement.id),
      );
      this.confirmingRemoveId.set(null);
    } catch (err) {
      this.removeError.set(this.errorMessageFor(err));
    } finally {
      this.removing.set(false);
    }
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      // Sequential, not Promise.all — keeps zoneless test timing predictable
      // (matches the established pattern elsewhere, e.g. CompetitionDetail.load()).
      const competition = await this.competitions.getOne(this.competitionId);
      this.competition.set(competition);
      const achievements = await this.competitions.listAchievements(
        this.competitionId,
      );
      this.achievements.set(achievements);
      // canManage() reads isOwner()/competition(), both already fresh from
      // the sets above — only fetch the catalog and leaderboard when
      // they'll actually be used (a non-owner, or a FREE owner, never
      // sees the add/award UI these back).
      if (this.canManage()) {
        const definitions = await this.competitions.listAchievementDefinitions();
        this.definitions.set(definitions);
        const leaderboard = await this.competitions.getLeaderboard(
          this.competitionId,
        );
        this.leaderboard.set(leaderboard);
      }
    } catch {
      this.errorMessage.set(
        'Could not load achievements — check the link or try again.',
      );
    } finally {
      this.loading.set(false);
    }
  }

  private errorMessageFor(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 409 || err.status === 403 || err.status === 400) {
        const message = (err.error as { message?: string } | null)?.message;
        if (message) return message;
      }
    }
    return 'Something went wrong — try again in a moment.';
  }
}
